import React, { useContext, useState } from "react";
import {
  Container,
  Box,
  Avatar,
  Typography,
  Grid,
  Paper,
  Button,
  IconButton,
  TextField,
  Stack,
  Modal,
  Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useForm } from "react-hook-form";
import { UserContext } from "../../context/UserProvider";
import MuPb from "../../widgets/MuPb";
import BackBtn from "../../widgets/BackBtn";
import { getHeader } from "../../utils/util";
import { ADMIN, API_ENDPOINT } from "../../utils/constants";

// Modal style
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: "8px",
};

const API_BASE_URL = "/api"; // Adjust this based on your backend setup

const AdminProfilePage = () => {
  const [open, setOpen] = useState(false);
  const { user, loading } = useContext(UserContext);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [modalSuccess, setModalSuccess] = useState("");
  const [modalError, setModalError] = useState("");

  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    reset: resetProfile,
  } = useForm();
  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    watch,
    reset: resetPassword,
    formState: { errors },
  } = useForm();

  const newPassword = watch("newPassword");

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setModalSuccess("");
    setModalError("");
    resetProfile();
  };

  // Submit handler for profile updates
  const onProfileSubmit = async (data) => {
    try {
      setModalError("");
      setModalSuccess("");

      const response = await fetch(
        `${API_ENDPOINT}/admin/update-admin-profile`,
        {
          method: "POST",
          ...getHeader(ADMIN),
          body: JSON.stringify({
            name: data.fullName,
            email: data.email,
            phone: data.phone,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update profile");
      }

      const result = await response.json();
      setModalSuccess(result.message || "Profile updated successfully");
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      setModalError(err.message || "An error occurred while updating profile");
    }
  };

  // Submit handler for password change
  const onPasswordSubmit = async (data) => {
    console.log("Password form submitted with data:", data); // Debug log

    // Client-side password match validation
    if (data.newPassword !== data.confirmPassword) {
      setError("New password and confirm password do not match");
      setSuccess("");
      return;
    }

    try {
      setError("");
      setSuccess("");

      console.log("Sending request to:", `${API_BASE_URL}/change-password`); // Debug log
      const response = await fetch(`${API_ENDPOINT}/admin/change-password`, {
        method: "POST",
        ...getHeader(ADMIN),
        body: JSON.stringify({
          currentPassword: data.currentPassword,
          newPassword: data.newPassword,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to change password");
      }

      const result = await response.json();
      setSuccess(result.message || "Password changed successfully");
      resetPassword();
    } catch (err) {
      console.error("Password submission error:", err); // Debug log
      setError(err.message || "An error occurred while changing password");
    }
  };

  if (loading) {
    return <MuPb />;
  }

  if (!user) {
    return <Alert severity="error">No user data found!</Alert>;
  }

  return (
    <Box>
      <BackBtn />
      <Container maxWidth="md">
        <Box sx={{ mt: 5, mb: 5 }}>
          <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
            {/* Profile Header */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box display="flex" alignItems="center">
                <Avatar
                  src={user.profilePic}
                  alt={user.name}
                  sx={{ width: 80, height: 80, mr: 2 }}
                >
                  {user.name.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="h5">{user.name}</Typography>
                  <Typography variant="subtitle1">
                    {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                  </Typography>
                  <Typography variant="body2">{user.email}</Typography>
                </Box>
              </Box>
              <IconButton onClick={handleOpen}>
                <EditIcon />
              </IconButton>
            </Box>

            {/* Profile Details */}
            <Box mt={4}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Full Name:</Typography>
                  <Typography variant="body1">{user.name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Email:</Typography>
                  <Typography variant="body1">{user.email}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Phone:</Typography>
                  <Typography variant="body1">{user.phone}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Role:</Typography>
                  <Typography variant="body1">
                    {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Change Password Section */}
            <Box mt={4}>
              <Typography variant="h6" gutterBottom>
                Change Password
              </Typography>
              <form onSubmit={handlePasswordSubmit(onPasswordSubmit)}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      type="password"
                      label="Current Password"
                      {...registerPassword("currentPassword", {
                        required: "Current password is required",
                      })}
                      error={!!errors.currentPassword}
                      helperText={errors.currentPassword?.message}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      type="password"
                      label="New Password"
                      {...registerPassword("newPassword", {
                        required: "New password is required",
                        minLength: {
                          value: 6,
                          message: "Password must be at least 6 characters",
                        },
                      })}
                      error={!!errors.newPassword}
                      helperText={errors.newPassword?.message}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      type="password"
                      label="Confirm New Password"
                      {...registerPassword("confirmPassword", {
                        required: "Please confirm your new password",
                        validate: (value) =>
                          value === newPassword || "Passwords do not match",
                      })}
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword?.message}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={2}>
                      <Button variant="contained" color="primary" type="submit">
                        Change Password
                      </Button>
                    </Stack>
                    {error && (
                      <Typography variant="body2" color="error" mt={1}>
                        {error}
                      </Typography>
                    )}
                    {success && (
                      <Typography variant="body2" color="primary" mt={1}>
                        {success}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </form>
            </Box>
          </Paper>
        </Box>

        {/* Modal for Edit Profile */}
        <Modal open={open} onClose={handleClose}>
          <Box sx={modalStyle}>
            <Typography variant="h6" mb={2}>
              Edit Profile
            </Typography>
            {modalSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {modalSuccess}
              </Alert>
            )}
            {modalError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {modalError}
              </Alert>
            )}
            <form onSubmit={handleProfileSubmit(onProfileSubmit)}>
              <TextField
                size="small"
                fullWidth
                label="Full Name"
                defaultValue={user.name}
                {...registerProfile("fullName", { required: true })}
                sx={{ mb: 2 }}
              />
              <TextField
                size="small"
                fullWidth
                label="Email"
                type="email"
                defaultValue={user.email}
                {...registerProfile("email", { required: true })}
                sx={{ mb: 2 }}
              />
              <TextField
                size="small"
                fullWidth
                label="Phone"
                defaultValue={user.phone}
                {...registerProfile("phone")}
                sx={{ mb: 2 }}
              />
              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button variant="contained" type="submit">
                  Save
                </Button>
                <Button variant="outlined" onClick={handleClose}>
                  Cancel
                </Button>
              </Stack>
            </form>
          </Box>
        </Modal>
      </Container>
    </Box>
  );
};

export default AdminProfilePage;
